import {Component, Injector, OnDestroy, OnInit} from '@angular/core';
import {FormArray, FormGroup} from '@angular/forms';

import {FrontendFormComponent} from '@abstract/frontend.form.component';
import {WizardService} from './services/wizard.service';
import {ActivatedRoute, Router} from '@angular/router';
import {WizardFormService} from './services/wizard-form.service';
import {WizardStep} from './wizard-step';
import {WizardGroup} from './wizard-group';
import {ComplianceCalculateService} from './services/wizard-form-groups-services/compliance-calculate.service';
import {ComplianceService} from './services/wizard-form-groups-services/compliance.service';
import {DataStorageService} from '@helpers/services/data-storage.service';
import {WizardEnums} from './wizard.namespace';
import {NtdService} from './services/wizard-form-groups-services/ntd.service';
import {OrganizationCommonService} from './services/wizard-form-groups-services/nested-forms/organization-common.service';
import {OrganizationLegalService} from './services/wizard-form-groups-services/nested-forms/organization-legal.service';
import {OrganizationBankService} from './services/wizard-form-groups-services/nested-forms/organization-bank.service';
import {OrganizationContactsService} from './services/wizard-form-groups-services/nested-forms/organization-contacts.service';
import {LaboratoryTypeService} from './services/wizard-form-groups-services/nested-forms/laboratory-type.service';
import {LaboratoryOwnerService} from './services/wizard-form-groups-services/nested-forms/laboratory-owner.service';
import {LaboratoryCommonService} from './services/wizard-form-groups-services/nested-forms/laboratory-common.service';
import {WizardFormEventsService} from './services/wizard-form-groups-services/wizard-form-events.service';
import {AttestationService} from './services/wizard-form-groups-services/nested-forms/attestation.service';
import {FormHelperService} from '@services/form-helper.service';
import {LaboratoryStaffService} from './services/wizard-form-groups-services/nested-forms/laboratory-staff.service';
import {EquipmentService} from './services/wizard-form-groups-services/nested-forms/equipment.service';
import {EquipmentApiService} from './services/wizard-form-groups-services/nested-forms/api/equipment-api.service';
import {ResponsibleStaffService} from './services/wizard-form-groups-services/nested-forms/responsible-staff.service';
import {EventsService} from '@services/events.service';
import {ConclusionsService} from './services/wizard-form-groups-services/nested-forms/conclusions.service';
import {ResultService} from './services/wizard-form-groups-services/nested-forms/result.service';
import {MethodsProvider} from '@providers/methods/methods.provider';
import {ErrorLoggerService} from '@services/error-logger.service';
import {WizardControlService} from './services/wizard-control.service';
import {ScrollToService} from '@services/scroll-to.service';

@Component({
    providers: [
        WizardFormService,
        ComplianceCalculateService,
        ComplianceService,
        DataStorageService,
        NtdService,
        WizardControlService,

        WizardFormEventsService,

        OrganizationCommonService,
        OrganizationLegalService,
        OrganizationBankService,
        OrganizationContactsService,
        LaboratoryTypeService,
        LaboratoryOwnerService,
        LaboratoryCommonService,
        AttestationService,
        LaboratoryStaffService,
        EquipmentService,
        EquipmentApiService,
        ResponsibleStaffService,
        ConclusionsService,
        ResultService,
    ],
    selector: 'wizard',
    templateUrl: './wizard.component.html',
    styleUrls: ['./wizard.component.scss']
})
export class WizardComponent extends FrontendFormComponent implements OnDestroy, OnInit {
    public form = new FormGroup({});

    public WS: WizardStep[] = [];
    public WG: WizardGroup[] = [];
    public CWS: WizardStep = null;

    public thankYouStep = new WizardStep('Пакет отправлен на проверку');

    private subs = [];

    constructor(
        public wizardFormService: WizardFormService,
        public formService: WizardService,
        injector: Injector,
        private route: ActivatedRoute,
        private router: Router,
        private stepsStorage: DataStorageService,
        private events: EventsService,
        private logger: ErrorLoggerService,
        private methodsProvider: MethodsProvider,
        private wizardControl: WizardControlService,
    ) {
        super(injector);
    }

    setStepTouched(step: WizardStep) {
        step.setTouched();

        if (step.form) {
            FormHelperService.validateGroup(step.form);
        }
    }

    init() {
        const { formValues } = this.route.snapshot.data;

        this.form = this.wizardFormService.form;

        try {
            this.setFormValues(formValues, this.form);
        } catch (e) {
            this.logger.log(e, 'Cannot setFormValuesForWizard');
        }

        this.wizardFormService.afterDataRestoreHandler();

        /**
         * Некоторые шаги сохраняем в storage, так как к ним будет программный доступ вне основной навигации
         */
        this.stepsStorage.set(
            WizardEnums.Steps.organizationCommon,
            new WizardStep('Данные организации', this.form.get('organization').get('common') as FormGroup),
        );

        this.stepsStorage.set(
            WizardEnums.Steps.organizationContacts,
            new WizardStep('Контактные данные', this.form.get('organization').get('contacts') as FormGroup),
        );

        this.stepsStorage.set(
            WizardEnums.Steps.attestation,
            new WizardStep('Область аттестации', this.form.get('attestation') as FormGroup)
        );


        const equipmentFormArray = this.form.get('equipment') as FormArray;

        const equipmentSteps = this.createEquipmentSteps(equipmentFormArray);

        const referenceFormArray = this.form.get('reference') as FormArray;

        const referenceSteps = this.createEquipmentSteps(referenceFormArray);

        const resultStep = new WizardStep('Результат', this.form.get('result') as FormGroup);

        /**
         * Формируем группы с шагами
         */
        this.WG = [
            new WizardGroup('Данные организации', [
                this.stepsStorage.get(WizardEnums.Steps.organizationCommon),
                new WizardStep('Юридические реквизиты', this.form.get('organization').get('legal') as FormGroup),
                new WizardStep('Банковские реквизиты', this.form.get('organization').get('bank') as FormGroup),
                this.stepsStorage.get(WizardEnums.Steps.organizationContacts),
            ], 'Организация'),
            new WizardGroup('Тип лаборатории', [
                new WizardStep('Тип лаборатории', this.form.get('laboratory').get('type') as FormGroup),
            ]),
            new WizardGroup('Руководитель лаборатории', [
                new WizardStep('Руководитель лаборатории', this.form.get('laboratory').get('owner') as FormGroup),
            ]),
            new WizardGroup('Общие данные лаборатории', [
                new WizardStep('Общие данные лаборатории', this.form.get('laboratory').get('common') as FormGroup),
            ]),
            new WizardGroup('Область аттестации', [
                this.stepsStorage.get(WizardEnums.Steps.attestation),
            ]),
            new WizardGroup('Персонал лаборатории', [
                new WizardStep('Персонал лаборатории', this.form.get('laboratory_staff') as FormGroup),
            ]),
            new WizardGroup('Ответственные лица', [
                new WizardStep('Ответственные лица', this.form.get('responsible_staff') as FormGroup),
            ]),
            new WizardGroup('Оборудование', equipmentSteps, 'Оборудование'),
            new WizardGroup('Образцы и эталоны', referenceSteps, 'Образцы и эталоны'),
            new WizardGroup('Нормативно-техническая документация', [
                new WizardStep('Общие', this.form.get('ntd_common') as FormGroup),
                new WizardStep('По методам', this.form.get('ntd_methods') as FormGroup, 'Методы'),
                new WizardStep('По объектам', this.form.get('ntd_objects') as FormGroup, 'Объекты'),
            ], 'НТД'),
            new WizardGroup('Заключительные положения', [
                new WizardStep('Заключительные положения', this.form.get('conclusions') as FormGroup),
            ]),
            new WizardGroup('Результат', [
                resultStep
            ]),
        ];

        /**
         * Достаем шаги из группы как коллекцию для удобства работы
         */
        this.WS = this.WG.reduce((acc, g) => {
            return acc.concat(g.steps);
        }, []);

        this.thankYouStep.setSteps(this.WS.concat([this.thankYouStep]));

        /**
         * Уведомлеяем все шаги о других соседних шагах
         */
        this.WS.forEach(s => s.setSteps(this.WS));

        const fragment = this.route.snapshot.fragment;

        if (fragment && fragment === 'generate') {
            this.CWS = resultStep;
        } else {
            this.CWS = this.WS[0];
        }
        /**
         * Делаем первый шаг как отображаемый
         */
        this.CWS.setActiveStatus();

        this.subs.push(
            this.wizardControl.activateStep$.subscribe(step => this.activateStep(this.stepsStorage.get(step)))
        );
    }

    onPrevStep() {
        if (!this.CWS || !this.CWS.prevStep) {
            return;
        }
        this.activateStep(this.CWS.prevStep);
    }

    onNextStep() {
        if (!this.CWS || !this.CWS.nextStep) {
            return;
        }
        this.activateStep(this.CWS.nextStep);
    }

    activateStep(step: WizardStep) {
        if (!step || !step.visible) {
            if (step && step.nextStep) {
                this.activateStep(step.nextStep);
            }
            return;
        }
        this.setStepTouched(this.CWS);
        this.CWS = step;
        this.CWS.setActiveStatus();
        this.shadowSave();
        ScrollToService.toTop();
    }

    showThankYouStep() {
        /**
         * Раньше был скрытый шаг, теперь будет редирект, но шаг все равно оставим
         */
        this.router.navigate(['../']);
        this.events.notyShow$.next({
            type: 'success',
            content: 'Сформировали пакет документов',
            hideOnRouteChange: false,
        });
    }

    /**
     * Создать шаги для оборудования на основании данных формы
     * @param fbArray
     */
    private createEquipmentSteps(fbArray: FormArray) {
        const steps: WizardStep[] = [];

        fbArray.controls.forEach((group: FormGroup) => {
            try {
                const methodId = group.get('id').value;
                const method = this.methodsProvider.getById(+methodId);

                if (method) {
                    steps.push(new WizardStep(method.title, group, method.short_title));
                }
            } catch (e) {
                this.logger.log(e, `Cannot create WizardStep`);
            }

        });

        return steps;
    }

    /**
     * Обработчик кнопки 'Отправить'.
     * Валидируем текущий шаг, на котором находимся и делаем его '_touched'
     */
    saveWithoutSend(): void {
        this.events.notyHide$.next();

        this.WS.forEach(step => {
            this.setStepTouched(step);
        });

        this.formService.shadowSave({
            value: this.form.getRawValue(),
            valid: this.form.valid
        }).toPromise().then(() => {
            this.events.notyShow$.next({
                type: 'success',
                content: 'Данные успешно сохранены',
            });
        }, () => {
            this.events.showGeneralHttpError();
        });
    }

    /**
     * Обработчик кнопки 'Сохранить и отправить'.
     * Если после валидации визард не валидный, то только сохраняем.
     * Валидируем и делаем _touched все шаги визирда
     * @param {FormGroup} model
     */
    saveAndSend(): void {
        this.events.notyHide$.next();

        this.WS.forEach(step => {
            this.setStepTouched(step);
        });

        if (this.form.valid) {
            this.events.pagePreloaderShow$.next('Подождите, система формирует и отправляет документы');
        }

        this.formService.shadowSave({
            value: this.form.getRawValue(),
            valid: this.form.valid,
            sendMail: this.form.valid,
        }).toPromise().then(() => {
            if (this.form.valid) {
                this.showThankYouStep();
            } else {
                this.events.notyShow$.next({
                    type: 'warning',
                    content: `Перед отправкой проверьте правильность заполнения полей. <br>
                        Промежуточные данные успешно сохранены`,
                });
            }
            this.events.pagePreloaderHide$.next();
        }, () => {
            this.events.pagePreloaderHide$.next();
            this.events.showGeneralHttpError();
        });
    }

    /**
     * Функция фонового сохранения формы визарда.
     * От обычного сохранения отличается тем, что не проверяет валидацию ошибок, просто шлет запрос на backend.
     */
    private shadowSave() {
        this.formService.shadowSave({
            value: this.form.getRawValue(),
            valid: this.form.valid
        }).toPromise().then(() => {

        }, () => {

        });
    }

    ngOnDestroy() {
        this.subs.forEach(sub => sub.unsubscribe());
        this.subs = [];
    }
}
